export const INCOMING_ASSET_LEAD_MS = 760;
export function clampElapsed(elapsedMs, durationMs) {
    return Math.min(Math.max(elapsedMs, 0), durationMs);
}
export function getScoreSnapshot(scenario, elapsedMs) {
    const gameElapsedSeconds = getGameElapsedSeconds(scenario, elapsedMs);
    const clock = getGameClock(scenario, gameElapsedSeconds);
    let awayScore = 0;
    let homeScore = 0;
    const completedPeriod = gameElapsedSeconds / scenario.periodLengthSeconds;
    const isExactPeriodBoundary = gameElapsedSeconds > 0 &&
        gameElapsedSeconds < scenario.periodLengthSeconds * 4 &&
        Number.isInteger(completedPeriod);
    for (const [period, clockSeconds, candidateAway, candidateHome] of scenario.scoreEvents) {
        const eventGameElapsed = toGameElapsedSeconds(period, clockSeconds, scenario.periodLengthSeconds);
        const startsNextPeriodAtBoundary = isExactPeriodBoundary &&
            period === completedPeriod + 1 &&
            clockSeconds === scenario.periodLengthSeconds;
        if (eventGameElapsed > gameElapsedSeconds || startsNextPeriodAtBoundary) {
            break;
        }
        awayScore = candidateAway;
        homeScore = candidateHome;
    }
    return {
        clock,
        awayScore,
        homeScore,
        statusLabel: elapsedMs <= 0
            ? "Pregame"
            : elapsedMs >= scenario.replayDurationMs
                ? "Final"
                : "Live",
        storyBeat: getStoryBeat(scenario, elapsedMs)
    };
}
export function getGameElapsedSeconds(scenario, elapsedMs) {
    const replayAtMs = clampElapsed(elapsedMs, scenario.replayDurationMs);
    const anchors = scenario.replayAnchors;
    if (replayAtMs <= anchors[0].replayAtMs) {
        return anchorToGameElapsed(anchors[0], scenario.periodLengthSeconds);
    }
    for (let index = 1; index < anchors.length; index += 1) {
        const next = anchors[index];
        if (replayAtMs > next.replayAtMs) {
            continue;
        }
        const previous = anchors[index - 1];
        const segmentDuration = next.replayAtMs - previous.replayAtMs;
        const ratio = segmentDuration === 0
            ? 1
            : (replayAtMs - previous.replayAtMs) / segmentDuration;
        const previousGameElapsed = anchorToGameElapsed(previous, scenario.periodLengthSeconds);
        const nextGameElapsed = anchorToGameElapsed(next, scenario.periodLengthSeconds);
        return previousGameElapsed + (nextGameElapsed - previousGameElapsed) * ratio;
    }
    return anchorToGameElapsed(anchors[anchors.length - 1], scenario.periodLengthSeconds);
}
export function getVisibleAssets(scenario, elapsedMs) {
    return [...scenario.feedAssets]
        .filter((asset) => asset.revealAtMs <= elapsedMs)
        .sort((left, right) => right.revealAtMs - left.revealAtMs);
}
export function getIncomingAsset(scenario, elapsedMs) {
    return [...scenario.feedAssets]
        .filter((asset) => elapsedMs >= asset.revealAtMs - INCOMING_ASSET_LEAD_MS &&
        elapsedMs < asset.revealAtMs)
        .sort((left, right) => left.revealAtMs - right.revealAtMs)[0];
}
export function getProgress(elapsedMs, durationMs) {
    if (durationMs <= 0) {
        return 0;
    }
    return clampElapsed(elapsedMs, durationMs) / durationMs;
}
export function getRegulationProgress(period, clockSeconds, periodLengthSeconds) {
    return (toGameElapsedSeconds(period, clockSeconds, periodLengthSeconds) /
        (periodLengthSeconds * 4));
}
export function formatReplayTime(elapsedMs, durationMs) {
    const value = Math.round(clampElapsed(elapsedMs, durationMs) / 1000);
    const total = Math.round(durationMs / 1000);
    return `${formatSeconds(value)} / ${formatSeconds(total)}`;
}
function formatSeconds(totalSeconds) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}
function anchorToGameElapsed(anchor, periodLengthSeconds) {
    return toGameElapsedSeconds(anchor.period, anchor.clockSeconds, periodLengthSeconds);
}
function toGameElapsedSeconds(period, clockSeconds, periodLengthSeconds) {
    return (period - 1) * periodLengthSeconds + (periodLengthSeconds - clockSeconds);
}
function getGameClock(scenario, gameElapsedSeconds) {
    const totalRegulationSeconds = scenario.periodLengthSeconds * 4;
    const clampedGameElapsed = Math.min(Math.max(gameElapsedSeconds, 0), totalRegulationSeconds);
    const isFinal = clampedGameElapsed >= totalRegulationSeconds;
    const completedPeriods = clampedGameElapsed / scenario.periodLengthSeconds;
    const isPeriodBoundary = !isFinal &&
        clampedGameElapsed > 0 &&
        Number.isInteger(completedPeriods);
    const period = isFinal
        ? 4
        : isPeriodBoundary
            ? completedPeriods
            : Math.floor(completedPeriods) + 1;
    const elapsedInPeriod = isFinal
        ? scenario.periodLengthSeconds
        : isPeriodBoundary
            ? scenario.periodLengthSeconds
            : clampedGameElapsed - (period - 1) * scenario.periodLengthSeconds;
    const clockSeconds = Math.max(0, scenario.periodLengthSeconds - elapsedInPeriod);
    return {
        period,
        periodLabel: isFinal
            ? "Final"
            : isPeriodBoundary
                ? period === 2
                    ? "Halftime"
                    : `End ${periodLabel(period)}`
                : periodLabel(period),
        displayClock: formatGameClock(clockSeconds)
    };
}
function getStoryBeat(scenario, elapsedMs) {
    let current = scenario.storyBeats[0]?.text ?? "The game is underway.";
    for (const beat of scenario.storyBeats) {
        if (beat.replayAtMs > elapsedMs) {
            break;
        }
        current = beat.text;
    }
    return current;
}
function periodLabel(period) {
    return ["1st", "2nd", "3rd", "4th"][period - 1] ?? `${period}th`;
}
function formatGameClock(clockSeconds) {
    const roundedTenths = Math.round(clockSeconds * 10) / 10;
    const minutes = Math.floor(roundedTenths / 60);
    const remainingSeconds = roundedTenths - minutes * 60;
    if (roundedTenths < 60) {
        return `${Math.floor(remainingSeconds)}.${Math.round((remainingSeconds % 1) * 10)}`;
    }
    return `${minutes}:${Math.floor(remainingSeconds).toString().padStart(2, "0")}`;
}
